import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useMemo, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { Loader } from '../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import { useSnackbar } from '../../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { PageOAuthRedirectProps } from '../../../../../OAuth/pages/Redirect';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties
} from '../common';
import { buildAuthorizeUrl } from './oauth';
import {
  connectSalesforceViaClientCredentials,
  disconnectSalesforce,
  storeSalesforceOAuthCredentials,
  useSalesforceConfig,
  useSalesforceCurrentUser
} from './service';

type AuthMethod = 'WEB_SERVER' | 'CLIENT_CREDENTIALS';

const AUTH_METHOD_OPTIONS: { label: string; value: AuthMethod; description: string }[] = [
  {
    label: 'OAuth user login (recommended)',
    value: 'WEB_SERVER',
    description:
      'Customer admin logs into Salesforce and clicks Allow. Writes attributed to that user in SF audit logs. Requires a callback URL on the Connected App.'
  },
  {
    label: 'Client Credentials (server-to-server)',
    value: 'CLIENT_CREDENTIALS',
    description:
      'No browser login, no callback URL. Requires the Connected App to have "Client Credentials Flow" enabled and a "Run As" user designated. All Dado writes appear as that Run As user.'
  }
];

const NAME = 'Salesforce';
const LOGO_URL = '/images/integrations-logos/salesforce.svg';

// Salesforce login URL options. Customers paste their Connected App credentials
// from EITHER their production org (login.salesforce.com or My Domain), OR a
// sandbox (test.salesforce.com). The login URL drives both the authorize URL
// the admin is redirected to AND the token endpoint the backend uses to refresh.
const LOGIN_URL_OPTIONS = [
  { label: 'Production (login.salesforce.com)', value: 'https://login.salesforce.com' },
  { label: 'Sandbox (test.salesforce.com)', value: 'https://test.salesforce.com' },
  { label: 'My Domain (custom URL)', value: 'CUSTOM' }
];

export const SALESFORCE_OAUTH_REDIRECT_PROPS: PageOAuthRedirectProps = {
  integrationName: NAME,
  cloudFn: 'salesforce_exchangeCodeAndStoreCredentials'
};

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const InitialSetupDialog = ({
  orgId,
  open,
  onClose,
  onConnected
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
  onConnected: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  const redirectUri = useMemo(
    () => `${window.location.origin}/integrations/salesforce/oauth`,
    []
  );

  const { enqueueSnackbarSuccess } = useSnackbar();
  const [authMethod, setAuthMethod] = useState<AuthMethod>('WEB_SERVER');
  const [loginEnvironment, setLoginEnvironment] = useState<string>(
    LOGIN_URL_OPTIONS[0].value
  );
  const [customLoginUrl, setCustomLoginUrl] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const effectiveLoginUrl =
    loginEnvironment === 'CUSTOM' ? customLoginUrl.trim() : loginEnvironment;

  const canSubmit =
    !!effectiveLoginUrl &&
    /^https:\/\//.test(effectiveLoginUrl) &&
    !!clientId.trim() &&
    !!clientSecret.trim();

  const onSaveAndAuthorize = async () => {
    setErrorMessage(null);
    if (authMethod === 'CLIENT_CREDENTIALS') {
      // Direct token exchange — no redirect, no callback URL. The callable
      // fetches the access token + identity in one round-trip.
      try {
        await connectSalesforceViaClientCredentials({
          orgId,
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
          loginUrl: effectiveLoginUrl
        });
        enqueueSnackbarSuccess(t('Salesforce integration connected.'));
        onConnected();
        onClose();
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? String((e as { message: string }).message)
            : 'Failed to connect via Client Credentials.';
        setErrorMessage(msg);
      }
      return;
    }
    // Web Server flow — save pending creds, then redirect to SF authorize.
    await storeSalesforceOAuthCredentials({
      orgId,
      clientId: clientId.trim(),
      clientSecret: clientSecret.trim(),
      redirectUri,
      loginUrl: effectiveLoginUrl
    });
    // New tab so the admin doesn't lose the apps state, matches how OAuthSetupDialog handles other integrations.
    window.open(buildAuthorizeUrl(effectiveLoginUrl, clientId.trim(), redirectUri, orgId), '_blank');
  };

  const selectedAuthOption = AUTH_METHOD_OPTIONS.find((o) => o.value === authMethod);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t(
            'Salesforce uses your own Connected App. Provision a Connected App in your Salesforce org, then paste the Consumer Key + Consumer Secret here.'
          )}
        </Alert>

        <TextField
          select
          label={t('Auth method')}
          value={authMethod}
          onChange={(e) => {
            setAuthMethod(e.target.value as AuthMethod);
            setErrorMessage(null);
          }}
          helperText={selectedAuthOption?.description}
        >
          {AUTH_METHOD_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label={t('Salesforce environment')}
          value={loginEnvironment}
          onChange={(e) => setLoginEnvironment(e.target.value)}
        >
          {LOGIN_URL_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        {loginEnvironment === 'CUSTOM' && (
          <TextField
            label={t('Custom login URL (My Domain)')}
            placeholder="https://yourcompany.my.salesforce.com"
            value={customLoginUrl}
            onChange={(e) => setCustomLoginUrl(e.target.value)}
            helperText={t('Must start with https:// and match your My Domain URL exactly.')}
          />
        )}

        <TextField
          label={t('Consumer Key')}
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          autoComplete="off"
        />

        <TextField
          label={t('Consumer Secret')}
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          type="password"
          autoComplete="new-password"
        />

        {authMethod === 'WEB_SERVER' && (
          <IntegrationData>
            <strong>{t('Callback URL')}</strong>
            <div>
              {redirectUri}
              <Typography variant="caption" component="div">
                {t('Add this exact URL to your Connected App\'s allowed callback URLs.')}
              </Typography>
            </div>
          </IntegrationData>
        )}

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      </Content>
      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              disabled={!canSubmit}
              onClick={onSaveAndAuthorize}
              pending={
                authMethod === 'CLIENT_CREDENTIALS'
                  ? `${t('Connecting')}...`
                  : `${t('org.integrations.actions.redirecting')}...`
              }
            >
              {authMethod === 'CLIENT_CREDENTIALS'
                ? t('Connect')
                : t('org.integrations.actions.setup')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

const ExistingSetupDialog = ({
  orgId,
  open,
  onClose,
  onDisconnected
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
  onDisconnected: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { id: userId } = useCurrentUser();

  const [user, loading, error] = useSalesforceCurrentUser(orgId);
  const [config] = useSalesforceConfig(orgId, userId);
  useErrorLogger('salesforce integration', error);

  // Undefined authMethod predates SF-30; treat as WEB_SERVER for back-compat.
  // Cast until the next @dadohr/shared bundle (which carries the field on
  // SalesforceIntegrationConfig directly) is published — see SF-30.1.
  const authMethod =
    (config?.data as { authMethod?: 'WEB_SERVER' | 'CLIENT_CREDENTIALS' })?.authMethod ??
    'WEB_SERVER';
  const authMethodLabel =
    authMethod === 'CLIENT_CREDENTIALS' ? t('Client Credentials') : t('OAuth user login');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        {loading && !user && <Loader height={300} />}
        {user && (
          <>
            <Alert severity="info" icon={false}>
              {t('Salesforce is connected.')}
            </Alert>
            <IntegrationData>
              <strong>{t('Auth method')}</strong>
              <div>{authMethodLabel}</div>
              <strong>{t('Connected user')}</strong>
              <div>
                {user.displayName} ({user.salesforceUserEmail})
              </div>
              <strong>{t('Salesforce user ID')}</strong>
              <div>{user.salesforceUserId}</div>
              <strong>{t('Salesforce org ID')}</strong>
              <div>{user.salesforceOrganizationId}</div>
              <strong>{t('Instance URL')}</strong>
              <div>{user.instanceUrl}</div>
              <strong>{t('Connected at')}</strong>
              <div>
                {user.connectedAt
                  ? new Date(user.connectedAt).toLocaleString()
                  : ''}
              </div>
            </IntegrationData>
          </>
        )}
        {error && <SomethingWentWrong />}
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={async () => {
              await disconnectSalesforce(orgId);
              onDisconnected();
              onClose();
            }}
            pending={`${t('Removing')}...`}
          >
            {t('Disconnect')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

interface SalesforceSetupDialogProps extends IntegrationSetupDialogProperties {
  orgId: string;
  connected: boolean;
  onDisconnected: () => void;
  onConnected: () => void;
}

const SetupDialog = ({
  orgId,
  open,
  onClose,
  connected,
  onDisconnected,
  onConnected
}: SalesforceSetupDialogProps) => {
  if (!connected) {
    return (
      <InitialSetupDialog
        orgId={orgId}
        open={open}
        onClose={onClose}
        onConnected={onConnected}
      />
    );
  }
  return (
    <ExistingSetupDialog
      orgId={orgId}
      open={open}
      onClose={onClose}
      onDisconnected={onDisconnected}
    />
  );
};

export const SalesforceSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { org } = useCurrentUser();

  // currentUser doubles as a "connected?" probe. failed-precondition
  // (NOT_CONNECTED) and unauthenticated (REAUTH_REQUIRED) both surface as
  // `error`; a missing user means "show the connect dialog".
  const [user, , error, refresh] = useSalesforceCurrentUser(org.id);
  const connected = !!user && !error;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl={LOGO_URL}
      active={connected}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onSuccessfulIntegration,
        onFailure
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          orgId={org.id}
          open={open}
          onClose={onClose}
          connected={connected}
          onDisconnected={refresh}
          onConnected={refresh}
          onSuccessfulIntegration={onSuccessfulIntegration}
          onFailure={onFailure}
        />
      )}
    />
  );
};
